import { useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ETAPES_ENTONNOIR,
  LIBELLES_ETAPE,
  LIBELLES_PERIODE,
  PERIODES_STATS,
  dureeLisible,
  pourcentage,
  taux,
  type AnnonceResume,
  type PeriodeStats,
  type StatsRecrutement,
} from '@rh/shared';
import { appel } from '../api';
import { BoutonExport } from '../composants/BoutonExport';
import { Chargement, Erreur, Vide } from '../composants/Etat';
import { Pastille } from '../composants/Pastille';
import { libelleSource } from '../format';

const nombre = (valeur: number) => valeur.toLocaleString('fr-FR');
const note = (valeur: number | null) =>
  valeur === null ? '—' : valeur.toLocaleString('fr-FR', { maximumFractionDigits: 1 });
/** Écart signé, avec un vrai signe moins. */
const ecartSigne = (valeur: number) =>
  `${valeur > 0 ? '+' : valeur < 0 ? '−' : ''}${note(Math.abs(valeur))}`;

/** Statistiques de pilotage (docs/ROADMAP.md point 22), sans donnée nominative. */
export function Statistiques() {
  const [parametres, setParametres] = useSearchParams();
  const annonceId = parametres.get('annonce') ?? '';
  const periode = (PERIODES_STATS as readonly string[]).includes(parametres.get('periode') ?? '')
    ? (parametres.get('periode') as PeriodeStats)
    : 'tout';

  const [annonces, setAnnonces] = useState<AnnonceResume[]>([]);
  const [stats, setStats] = useState<StatsRecrutement | null>(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    appel<AnnonceResume[]>('/annonces')
      .then((liste) => setAnnonces(liste.filter((a) => a.statut !== 'brouillon')))
      .catch(() => setAnnonces([]));
  }, []);

  useEffect(() => {
    // La vue précédente reste affichée, estompée, pendant le rechargement.
    setChargement(true);
    const requete = new URLSearchParams({ periode });
    if (annonceId) requete.set('annonce', annonceId);
    appel<StatsRecrutement>(`/stats?${requete.toString()}`)
      .then((resultat) => {
        setStats(resultat);
        setErreur(null);
      })
      .catch((e: Error) => setErreur(e.message))
      .finally(() => setChargement(false));
  }, [periode, annonceId]);

  function filtrer(cle: 'annonce' | 'periode', valeur: string): void {
    const suivants = new URLSearchParams(parametres);
    if (valeur) suivants.set(cle, valeur);
    else suivants.delete(cle);
    setParametres(suivants, { replace: true });
  }

  return (
    <section className="statistiques">
      <div className="entete-annonce">
        <h2>Statistiques</h2>
        {stats && stats.entonnoir.recues > 0 && (
          <BoutonExport
            chemin={`/exports/statistiques?${new URLSearchParams({ periode, ...(annonceId ? { annonce: annonceId } : {}) }).toString()}`}
            nomParDefaut="rh-statistiques.xlsx"
          />
        )}
      </div>

      <div className="filtres-stats">
        <div className="filtres" role="group" aria-label="Période de réception">
          {PERIODES_STATS.map((p) => (
            <button key={p} type="button" className={p === periode ? 'actif' : ''} onClick={() => filtrer('periode', p)}>
              {LIBELLES_PERIODE[p]}
            </button>
          ))}
        </div>
        <select value={annonceId} aria-label="Annonce" onChange={(e) => filtrer('annonce', e.target.value)}>
          <option value="">Toutes les annonces</option>
          {annonces.map((a) => (
            <option key={a.id} value={a.id}>
              {a.reference} — {a.intitule}
            </option>
          ))}
        </select>
      </div>

      {erreur && <Erreur message={erreur} />}
      {!stats && chargement && <Chargement quoi="des statistiques" />}
      {stats && (
        <div className={chargement ? 'vue-stats rafraichissement' : 'vue-stats'} aria-busy={chargement}>
          {stats.entonnoir.recues === 0 ? (
            <Vide enfants="Aucune candidature reçue sur cette période." />
          ) : (
            <Tableau stats={stats} />
          )}
        </div>
      )}
    </section>
  );
}

function Tableau({ stats }: { stats: StatsRecrutement }) {
  const { entonnoir, premierContact, joignabilite, sources, ecartPastilleNote, sorties } = stats;
  const maxSource = Math.max(...sources.map((s) => s.recues), 1);

  return (
    <>
      <div className="tuiles">
        <Tuile libelle="Candidatures reçues" valeur={nombre(entonnoir.recues)}>
          {nombre(entonnoir.embauches)} embauchée{entonnoir.embauches > 1 ? 's' : ''} (
          {pourcentage(taux(entonnoir.embauches, entonnoir.recues))})
        </Tuile>
        <Tuile libelle="Délai médian de premier appel" valeur={dureeLisible(premierContact.medianeHeures)}>
          {nombre(premierContact.dansLeDelai)} sur {nombre(premierContact.avecContact)} appelées sous{' '}
          {premierContact.delaiHeures}&nbsp;h · {nombre(premierContact.sansContact)} jamais appelée
          {premierContact.sansContact > 1 ? 's' : ''}
        </Tuile>
        <Tuile
          libelle="Joignabilité des appels"
          valeur={pourcentage(taux(joignabilite.joints, joignabilite.appels))}
        >
          {nombre(joignabilite.joints)} appels aboutis sur {nombre(joignabilite.appels)} ·{' '}
          {pourcentage(taux(joignabilite.candidatsJoints, joignabilite.candidatsAppeles))} des candidats appelés
          joints
        </Tuile>
        <Tuile
          libelle="Écart note − pastille"
          valeur={
            ecartPastilleNote.ecartMoyen === null
              ? '—'
              : `${ecartSigne(ecartPastilleNote.ecartMoyen)}\u00a0pt`
          }
        >
          sur {nombre(ecartPastilleNote.paires)} entretien{ecartPastilleNote.paires > 1 ? 's' : ''} précédé
          {ecartPastilleNote.paires > 1 ? 's' : ''} d’une pastille, éliminatoires exclus
        </Tuile>
      </div>

      <section className="bloc">
        <h3>Entonnoir</h3>
        <p className="mention">
          Chaque candidature compte jusqu’à l’étape la plus avancée atteinte. Pourcentages rapportés aux
          candidatures reçues.
        </p>
        <ol className="entonnoir">
          {ETAPES_ENTONNOIR.map((etape, rang) => {
            const valeur = entonnoir[etape];
            const precedente = rang > 0 ? entonnoir[ETAPES_ENTONNOIR[rang - 1]!] : null;
            const part = taux(valeur, entonnoir.recues) ?? 0;
            return (
              <li key={etape} tabIndex={0} className="etape">
                <span className="etape-libelle">{LIBELLES_ETAPE[etape]}</span>
                <span className="etape-piste">
                  {valeur > 0 && <span className="etape-barre" style={{ width: `max(2px, calc((100% - 6.5rem) * ${part}))` }} />}
                  <span className="etape-valeur">
                    {nombre(valeur)} · {pourcentage(part)}
                  </span>
                </span>
                <span className="infobulle" role="tooltip">
                  <strong>{nombre(valeur)}</strong> {LIBELLES_ETAPE[etape].toLowerCase()}
                  {precedente !== null && ` · ${pourcentage(taux(valeur, precedente))} de l’étape précédente`}
                </span>
              </li>
            );
          })}
        </ol>
        <p className="mention">
          Sorties : {nombre(sorties.ecarte)} écartée{sorties.ecarte > 1 ? 's' : ''} ·{' '}
          {nombre(sorties.desistement)} désistement{sorties.desistement > 1 ? 's' : ''} ·{' '}
          {nombre(sorties.vivier)} au vivier
        </p>
      </section>

      <section className="bloc">
        <h3>Rendement par source</h3>
        <div className="defilement">
          <table className="table-stats">
            <thead>
              <tr>
                <th scope="col">Source</th>
                <th scope="col">Reçues</th>
                <th scope="col">Jointes</th>
                <th scope="col">Entretiens</th>
                <th scope="col">Embauchées</th>
                <th scope="col">Note moyenne</th>
              </tr>
            </thead>
            <tbody>
              {sources.map((s) => (
                <tr key={s.source}>
                  <th scope="row">{libelleSource(s.source)}</th>
                  <td>
                    <span className="cellule-barre">
                      <span className="mini-barre" style={{ width: `${(s.recues / maxSource) * 100}%` }} />
                      {nombre(s.recues)}
                    </span>
                  </td>
                  <td>
                    {nombre(s.contactees)} <span className="mention">{pourcentage(taux(s.contactees, s.recues))}</span>
                  </td>
                  <td>{nombre(s.entretiens)}</td>
                  <td>
                    {nombre(s.embauches)} <span className="mention">{pourcentage(taux(s.embauches, s.recues))}</span>
                  </td>
                  <td>{note(s.noteMoyenne)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="bloc">
        <h3>Pastille au téléphone, note à l’entretien</h3>
        {ecartPastilleNote.parPastille.length === 0 ? (
          <p className="mention">Aucun entretien noté après un appel avec pastille sur cette période.</p>
        ) : (
          <div className="defilement">
            <table className="table-stats">
              <thead>
                <tr>
                  <th scope="col">Pastille</th>
                  <th scope="col">Entretiens</th>
                  <th scope="col">Note moyenne</th>
                  <th scope="col">Écart</th>
                </tr>
              </thead>
              <tbody>
                {ecartPastilleNote.parPastille.map((ligne) => {
                  const ecart = ligne.noteMoyenne - ligne.pastille;
                  return (
                    <tr key={ligne.pastille}>
                      <th scope="row">
                        <Pastille valeur={ligne.pastille} />
                      </th>
                      <td>{nombre(ligne.nombre)}</td>
                      <td>{note(ligne.noteMoyenne)}</td>
                      <td>
                        {ecartSigne(ecart)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

function Tuile({ libelle, valeur, children }: { libelle: string; valeur: string; children: ReactNode }) {
  return (
    <div className="tuile">
      <span className="tuile-libelle">{libelle}</span>
      <span className="tuile-valeur">{valeur}</span>
      <span className="tuile-detail">{children}</span>
    </div>
  );
}
